import React, { useCallback, useEffect, useState } from "react";
import { Close as CloseIcon, Translate as TranslateIcon } from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";
import DictionaryApiService from "../../../admin/system/integrate/DictionaryApiService";
import WiktionaryAttribution from "../../../admin/pzl_common/WiktionaryAttribution";
import Logger from "../../../services/Logger";
import { usePagesTranslation } from "../../locales";

const partOfSpeechOrder = [
    "nom", "noun", "verbe", "verb", "adjectif", "adjective", "adverbe", "adverb", "title"
];

const sortByPartOfSpeech = (entries) =>
    [...entries].sort(([a], [b]) => {
        const indexA = partOfSpeechOrder.indexOf(a.toLowerCase());
        const indexB = partOfSpeechOrder.indexOf(b.toLowerCase());
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

const formatDefinitionBlock = (definition) => {
    if (typeof definition !== "string") return String(definition ?? "");
    return definition.replace(/(\d+\.\s)/g, "\n$1").trim();
};

const normLang = (lang) => (lang == null ? "" : String(lang).trim().toLowerCase());

const renderMeaningObject = (meaningObj) => {
    if (!meaningObj || typeof meaningObj !== "object") return null;
    return sortByPartOfSpeech(Object.entries(meaningObj))
        .filter(([key]) => key !== "title")
        .map(([partOfSpeech, definition], index, array) => (
            <Box key={partOfSpeech} sx={{ mb: 2 }}>
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", color: "secondary.main", mb: 1, textTransform: "capitalize" }}
                >
                    {partOfSpeech}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        whiteSpace: "pre-line",
                        lineHeight: 1.6,
                        color: "text.primary",
                        pl: 1
                    }}
                >
                    {formatDefinitionBlock(definition)}
                </Typography>
                {index < array.length - 1 && <Divider sx={{ mt: 2, opacity: 0.3 }} />}
            </Box>
        ));
};

const WordMeaningDialog = ({
    open,
    onClose,
    word,
    hideWord = false,
    language = "english",
    /** Interface language from `usePagesTranslation().lang` — when different from `language`, show translate control */
    uiLang,
    /** Optional title node (e.g. partial-word circles) instead of the spelled word. */
    titleContent = null,
}) => {
    const { t } = usePagesTranslation();

    const getUiLanguageLabel = useCallback(
        (lang) => {
            const k = normLang(lang);
            if (!k) return "";
            const path = `puzzle.wordMeaning.lang.${k}`;
            const label = t(path);
            if (label === path) return k.charAt(0).toUpperCase() + k.slice(1);
            return label;
        },
        [t]
    );

    const [wordData, setWordData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [translationResult, setTranslationResult] = useState(null);
    const [translationLoading, setTranslationLoading] = useState(false);
    const [translationError, setTranslationError] = useState(null);

    const contentLang = normLang(language);
    const targetLang = normLang(uiLang);
    const showTranslateButton = Boolean(open && uiLang && targetLang);
    const langsDiffer = Boolean(
        contentLang && targetLang && targetLang !== contentLang
    );
    const canRunTranslate = langsDiffer;

    useEffect(() => {
        const fetchDefinition = async () => {
            if (!open || !word || typeof word !== "string") {
                setWordData(null);
                setError(null);
                return;
            }
            const trimmed = word.trim();
            if (!trimmed) {
                setWordData(null);
                setError(null);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const result = await DictionaryApiService.getWordDefinition(trimmed, language);
                if (result && result.length > 0) {
                    setWordData(result[0]);
                } else {
                    setWordData(null);
                }
            } catch (err) {
                Logger.error("WordMeaningDialog: Error fetching word definition:", err);
                setError(t("puzzle.wordMeaning.load-failed"));
                setWordData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchDefinition();
    }, [open, word, language, t]);

    useEffect(() => {
        if (!open) return;
        setTranslationResult(null);
        setTranslationError(null);
        setTranslationLoading(false);
    }, [open, word, language, uiLang]);

    const handleTranslate = useCallback(async () => {
        if (!word || typeof word !== "string" || !canRunTranslate) return;
        const trimmed = word.trim();
        if (!trimmed) return;
        setTranslationLoading(true);
        setTranslationError(null);
        setTranslationResult(null);
        try {
            const result = await DictionaryApiService.translateWord(
                trimmed,
                contentLang,
                targetLang
            );
            if (result && (result.error || result.code === "FAILURE")) {
                const msg =
                    (typeof result.error === "string" && result.error) ||
                    result.message ||
                    t("puzzle.wordMeaning.translation-failed");
                setTranslationError(msg);
                return;
            }
            setTranslationResult(result);
        } catch (err) {
            Logger.error("WordMeaningDialog: translate error:", err);
            setTranslationError(err?.message || t("puzzle.wordMeaning.translation-failed"));
        } finally {
            setTranslationLoading(false);
        }
    }, [word, canRunTranslate, contentLang, targetLang, t]);

    const handleClose = () => {
        setWordData(null);
        setError(null);
        setTranslationResult(null);
        setTranslationError(null);
        setTranslationLoading(false);
        onClose();
    };

    const translatedBlock = canRunTranslate
        ? translationResult?.meaningsI18n?.[targetLang]
        : null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth={false}
            PaperProps={{
                sx: {
                    maxHeight: "90vh",
                    width: "680px",
                    overflow: "hidden",
                }
            }}
        >
            <DialogTitle
                sx={{
                    fontWeight: "bold",
                    color: "#3BA666",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    pr: 0.5,
                    minHeight: 56,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {titleContent || (
                    <Typography
                        component="span"
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            color: "inherit",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {hideWord ? t("puzzle.wordMeaning.title") : word?.toUpperCase() || ""}
                    </Typography>
                    )}
                    {showTranslateButton ? (
                        <Tooltip
                            title={
                                !langsDiffer
                                    ? t("puzzle.wordMeaning.translate-same-lang")
                                    : translationLoading
                                      ? t("puzzle.wordMeaning.translating")
                                      : t("puzzle.wordMeaning.translate", {
                                            language: getUiLanguageLabel(uiLang),
                                        })
                            }
                        >
                            <span>
                                <IconButton
                                    type="button"
                                    size="small"
                                    onClick={handleTranslate}
                                    disabled={translationLoading || !langsDiffer}
                                    aria-label={t("puzzle.wordMeaning.translate", {
                                        language: getUiLanguageLabel(uiLang),
                                    })}
                                    sx={{
                                        flexShrink: 0,
                                        color: "#2E8B57",
                                        "&:disabled": { color: "action.disabled" },
                                    }}
                                >
                                    {translationLoading ? (
                                        <CircularProgress size={20} sx={{ color: "#2E8B57" }} />
                                    ) : (
                                        <TranslateIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                </Box>
                <IconButton
                    aria-label={t("puzzle.wordMeaning.close-aria")}
                    onClick={handleClose}
                    sx={{
                        flexShrink: 0,
                        color: "#666",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            color: "#333",
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    overflowX: "hidden",
                    overflowY: "auto",
                    maxHeight: "calc(90vh - 120px)",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                }}
            >
                {word && (
                    <Box sx={{ p: 0 }}>
                        {loading && (
                            <Typography variant="body2" color="text.secondary">
                                {t("puzzle.wordMeaning.loading")}
                            </Typography>
                        )}
                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}
                        {!loading && !error && (!wordData || !wordData.meanings?.length) && (
                            <Typography variant="body2" color="text.secondary">
                                {t("puzzle.wordMeaning.no-definitions")}
                            </Typography>
                        )}
                        {!loading && !error && wordData?.meanings?.length > 0 && (
                            <>
                                {wordData.meanings.map((meaning, meaningIndex) => (
                                    <Box key={meaningIndex} sx={{ mb: 2 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: "bold", color: "secondary.main", mb: 1 }}
                                        >
                                            {meaning.partOfSpeech
                                                ? meaning.partOfSpeech.charAt(0).toUpperCase() +
                                                  (meaning.partOfSpeech.slice(1) || "").toLowerCase()
                                                : ""}
                                        </Typography>
                                        {meaning.definitions?.map((def, defIndex) => (
                                            <Box key={defIndex} sx={{ mb: 1.5, pl: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mb: 0.5,
                                                        whiteSpace: "pre-line",
                                                        lineHeight: 1.6,
                                                        color: "text.primary"
                                                    }}
                                                >
                                                    {def.definition?.replace(/(\d+\.\s)/g, "\n$1").trim()}
                                                </Typography>
                                                {def.example && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontStyle: "italic",
                                                            color: "text.secondary",
                                                            display: "block",
                                                            mt: 0.5,
                                                            pl: 1,
                                                            borderLeft: "2px solid",
                                                            borderColor: "divider"
                                                        }}
                                                    >
                                                        "{def.example}"
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                ))}

                                {canRunTranslate && (translationError || translationResult) && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}
                                        >
                                            {t("puzzle.wordMeaning.translation-heading", {
                                                language: getUiLanguageLabel(uiLang)
                                            })}
                                        </Typography>
                                        {translationError && (
                                            <Typography variant="body2" color="error">
                                                {translationError}
                                            </Typography>
                                        )}
                                        {!translationError &&
                                            translatedBlock &&
                                            renderMeaningObject(translatedBlock)}
                                        {!translationError &&
                                            translationResult &&
                                            !translatedBlock && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("puzzle.wordMeaning.translation-empty", {
                                                        language: getUiLanguageLabel(uiLang)
                                                    })}
                                                </Typography>
                                            )}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>
            <Box sx={{ px: 2, pb: 1.5, pt: 0 }}>
                <WiktionaryAttribution />
            </Box>
        </Dialog>
    );
};

export default WordMeaningDialog;
